// BORIS-Import-Dialog (Issue #27).
//
// Ablauf: Datei-Auswahl (.BSY + optional mehrere .SBL) → Ziel wählen (neues Projekt ODER aktuelles
// Projekt) → Upload → Abdeckungsbericht (welche Blöcke importiert / Platzhalter / ungelöste
// Superblöcke) → Graph + Katalog laden/anzeigen. Ein importiertes BORIS-System läuft über
// boris2py.generate_system (volle boris-Abdeckung); der abgeleitete Graph trägt die Darstellung.
//
// Christian-Entscheidungen: beim Import wählbar neues ODER aktuelles Projekt; best-effort mit
// Abdeckungsbericht; die Instanz ist lauffähig, sobald der Rechenpfad vollständig ist.

import { useCallback, useMemo, useRef, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { createProject, importBoris } from '../api/client';
import { ApiError } from '../api/client';
import type { BorisImportResult } from '../api/types';
import { Button, LabeledInput, PanelHeading } from '../components/ui';

// Ziel des Imports: ein frisch angelegtes Projekt oder das aktuell geöffnete.
type Target = 'new' | 'current';

export interface BorisImportDialogProps {
  // Das aktuell geöffnete Projekt (Ziel 'current'); null → nur 'new' möglich.
  currentProjectId: string | null;
  onClose: () => void;
  // Nach erfolgreichem Import: das (ggf. neue) Zielprojekt aktiv setzen und dessen Daten neu laden.
  onImported: (result: BorisImportResult) => void;
}

// kebab-case-Projekt-ID aus einem Anzeigenamen (klein, nur [a-z0-9-]).
function slugify(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function BorisImportDialog({
  currentProjectId,
  onClose,
  onImported,
}: BorisImportDialogProps): JSX.Element {
  const { t } = useI18n();
  const bsyRef = useRef<HTMLInputElement>(null);
  const sblRef = useRef<HTMLInputElement>(null);

  const [bsyFile, setBsyFile] = useState<File | null>(null);
  const [sblFiles, setSblFiles] = useState<File[]>([]);
  const [target, setTarget] = useState<Target>(currentProjectId ? 'current' : 'new');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BorisImportResult | null>(null);

  const newId = useMemo(() => slugify(newName), [newName]);

  const canImport =
    !!bsyFile &&
    !busy &&
    (target === 'current' ? !!currentProjectId : newId.length > 0) &&
    !result;

  const runImport = useCallback(async () => {
    if (!bsyFile) return;
    setBusy(true);
    setError(null);
    try {
      // Zielprojekt bestimmen: neues anlegen ODER das aktuelle nehmen.
      let projectId = currentProjectId ?? '';
      if (target === 'new') {
        const meta = await createProject({ id: newId, name: newName.trim() || newId });
        projectId = meta.id;
      }
      const res = await importBoris(projectId, bsyFile, sblFiles);
      setResult(res);
      onImported(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('boris.importFailed'));
    } finally {
      setBusy(false);
    }
  }, [bsyFile, sblFiles, target, currentProjectId, newId, newName, onImported, t]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('boris.title')}
      data-testid="boris-dialog"
    >
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col gap-4 overflow-y-auto rounded-lg border border-bf-border bg-bf-navy p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-bf-white">{t('boris.title')}</h2>
          <button
            type="button"
            aria-label={t('common.close')}
            data-testid="boris-close"
            onClick={onClose}
            className="text-bf-muted hover:text-bf-white"
          >
            ✕
          </button>
        </div>

        {!result && (
          <>
            <p className="text-xs text-bf-muted">{t('boris.intro')}</p>

            {/* Datei-Auswahl */}
            <div className="flex flex-col gap-2">
              <PanelHeading>{t('boris.files')}</PanelHeading>
              <div className="flex items-center gap-2">
                <Button onClick={() => bsyRef.current?.click()}>{t('boris.chooseBsy')}</Button>
                <span className="text-xs text-bf-white" data-testid="boris-bsy-name">
                  {bsyFile ? bsyFile.name : t('boris.noBsy')}
                </span>
                <input
                  ref={bsyRef}
                  type="file"
                  accept=".bsy,.BSY"
                  data-testid="boris-bsy-input"
                  className="hidden"
                  onChange={(e) => setBsyFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => sblRef.current?.click()}>{t('boris.chooseSbl')}</Button>
                <span className="text-xs text-bf-white" data-testid="boris-sbl-count">
                  {sblFiles.length > 0
                    ? t('boris.sblCount', { count: sblFiles.length })
                    : t('boris.noSbl')}
                </span>
                <input
                  ref={sblRef}
                  type="file"
                  accept=".sbl,.SBL"
                  multiple
                  data-testid="boris-sbl-input"
                  className="hidden"
                  onChange={(e) => setSblFiles(Array.from(e.target.files ?? []))}
                />
              </div>
            </div>

            {/* Ziel: neues ODER aktuelles Projekt */}
            <div className="flex flex-col gap-2">
              <PanelHeading>{t('boris.target')}</PanelHeading>
              <label className="flex items-center gap-2 text-sm text-bf-white">
                <input
                  type="radio"
                  name="boris-target"
                  data-testid="boris-target-new"
                  checked={target === 'new'}
                  onChange={() => setTarget('new')}
                />
                {t('boris.targetNew')}
              </label>
              {target === 'new' && (
                <span data-testid="boris-new-name">
                  <LabeledInput
                    label={t('boris.newName')}
                    value={newName}
                    onChange={setNewName}
                    placeholder={t('boris.newNamePlaceholder')}
                  />
                </span>
              )}
              <label
                className={
                  'flex items-center gap-2 text-sm ' +
                  (currentProjectId ? 'text-bf-white' : 'text-bf-muted')
                }
              >
                <input
                  type="radio"
                  name="boris-target"
                  data-testid="boris-target-current"
                  disabled={!currentProjectId}
                  checked={target === 'current'}
                  onChange={() => setTarget('current')}
                />
                {currentProjectId
                  ? t('boris.targetCurrent', { project: currentProjectId })
                  : t('boris.targetCurrentNone')}
              </label>
            </div>

            {error && (
              <p className="text-xs text-red-300" data-testid="boris-error">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button onClick={onClose}>{t('common.cancel')}</Button>
              <span data-testid="boris-import-run">
                <Button variant="primary" onClick={runImport} disabled={!canImport}>
                  {busy ? t('boris.importing') : t('boris.import')}
                </Button>
              </span>
            </div>
          </>
        )}

        {result && <CoverageReport result={result} onClose={onClose} />}
      </div>
    </div>
  );
}

// Abdeckungsbericht: importierte Blöcke, Platzhalter (nicht abgebildet) und ungelöste Superblöcke.
// Grün, wenn der Rechenpfad vollständig (runnable) ist — dann läuft die Instanz sofort.
function CoverageReport({
  result,
  onClose,
}: {
  result: BorisImportResult;
  onClose: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const { coverage } = result;
  return (
    <div className="flex flex-col gap-3" data-testid="boris-coverage">
      <div
        className={
          'rounded border p-2 text-sm ' +
          (coverage.runnable
            ? 'border-green-500/50 bg-green-950/30 text-green-200'
            : 'border-amber-500/50 bg-amber-950/30 text-amber-200')
        }
        data-testid="boris-runnable"
      >
        {coverage.runnable ? t('boris.runnable') : t('boris.notRunnable')}
      </div>

      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded border border-bf-border p-2">
          <div className="text-lg font-semibold text-bf-white" data-testid="boris-supported-count">
            {coverage.supported.length}
          </div>
          <div className="text-bf-muted">{t('boris.supported')}</div>
        </div>
        <div className="rounded border border-bf-border p-2">
          <div
            className="text-lg font-semibold text-bf-white"
            data-testid="boris-unsupported-count"
          >
            {coverage.unsupported.length}
          </div>
          <div className="text-bf-muted">{t('boris.unsupported')}</div>
        </div>
        <div className="rounded border border-bf-border p-2">
          <div
            className="text-lg font-semibold text-bf-white"
            data-testid="boris-unresolved-count"
          >
            {coverage.unresolved_superblocks.length}
          </div>
          <div className="text-bf-muted">{t('boris.unresolved')}</div>
        </div>
      </div>

      {coverage.unsupported.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="boris-unsupported-list">
          <PanelHeading>{t('boris.placeholders')}</PanelHeading>
          {coverage.unsupported.map((b) => (
            <div key={b.index} className="text-xs text-amber-300">
              #{b.index} · {b.type}
              {b.name ? ` · ${b.name}` : ''}
            </div>
          ))}
        </div>
      )}

      {coverage.unresolved_superblocks.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="boris-unresolved-list">
          <PanelHeading>{t('boris.unresolvedSuperblocks')}</PanelHeading>
          {coverage.unresolved_superblocks.map((name) => (
            <div key={name} className="text-xs text-amber-300">
              {name}
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-bf-muted">
        {t('boris.importedInto', { project: result.projectId })}
      </p>

      <div className="flex justify-end">
        <Button variant="primary" onClick={onClose}>
          {t('boris.done')}
        </Button>
      </div>
    </div>
  );
}
